<script lang="ts" setup>
import {computed} from 'vue';
import {Order, OrderItem, FactoryLabel} from '@teemill/common/models';
import {formatUrl} from '@teemill/common/helpers';
import {ShopReturn} from '../../../../../../packages/common/src/models/shopReturn/shopReturn';
import {OrderItemReturnLog} from '../../../../../../packages/common/src/models/order/orderItemReturnLog';

const props = defineProps<{
  order: Order;
  faultyOnly: boolean;
  showConfirmationPage: boolean;
  orderHash: string;
}>();

const isWholesaleOrder = computed<boolean>(() => {
  return (
    props.order.hasBeenIn('quote') ||
    props.order.division?.code === 'teemill' ||
    !props.order.isIntegration('teemill')
  );
});

const areAllItemsRefundedOrExchanged = computed<boolean>(() => {
  return activeItemsCount.value === 0;
});

const isOrderBeingShipped = computed<boolean>(() => {
  return (
    !props.order.hasBeenIn('complete') &&
    !props.faultyOnly &&
    !props.showConfirmationPage
  );
});

const unproccessedReturns = computed<ShopReturn[]>(() => {
  return props.order.shopReturns.filter(
    (shopReturn: ShopReturn) => !shopReturn.processedAt
  );
});

const hasShopReturns = computed<boolean>(() => {
  return props.order.shopReturns.length > 0;
});

const isOrderOldAndFaulty = computed<boolean>(() => {
  return props.faultyOnly && !props.showConfirmationPage;
});

const status = computed<{is: (status: string) => boolean}>(() => {
  return {
    is: (status: string) => props.order.status.code === status,
  };
});

const activeItemsCount = computed<number>(() => {
  let returnedCount = 0;
  let cancelledCount = 0;
  let originalQuantity = 0;

  props.order.items.forEach((item: OrderItem) => {
    item.returnLogs.forEach((log: OrderItemReturnLog) => {
      returnedCount += log.quantity;
    });

    cancelledCount += item.labels.filter(
      (label: FactoryLabel) => label.cancelled || label.cancelledAt
    ).length;

    if (item.optionMatrixRecord && item.optionMatrixRecord.id) {
      originalQuantity += item.quantity;
    }
  });

  const finalActiveItemsCount =
    originalQuantity - cancelledCount - returnedCount;

  return finalActiveItemsCount;
});

const hasUnprocessedReturns = computed<boolean>(() => {
  return unproccessedReturns.value.length > 0;
});

const isReturnLabelAvailable = computed(() => {
  return (
    props.order.hasBeenIn('complete') &&
    !(props.order.hasBeenIn('quote') || status.value.is('teemill')) &&
    status.value.is('teemill') &&
    !props.showConfirmationPage
  );
});
const isReturningSomeItems = computed<boolean>(() => {
  return (
    hasShopReturns.value &&
    hasUnprocessedReturns.value &&
    props.order.hasBeenIn('complete') &&
    !props.showConfirmationPage
  );
});

const isReturningAllItems = computed<boolean>(() => {
  return (
    props.order.hasBeenIn('complete') &&
    !(props.order.hasBeenIn('quote') || status.value.is('teemill')) &&
    status.value.is('teemill') &&
    !props.showConfirmationPage
  );
});
const labelType = computed<string>(() => {
  return props.order.shippingCountry.code === 'GB' ? 'freepost ' : '';
});

const returnsLabelFilename = computed<string>(() => {
  return `${
    props.order.division ? `${props.order.division.name.toLowerCase()}-` : ''
  }
  ${labelType.value ? `${labelType.value}-` : ''}returns-label.pdf`;
});
</script>

<template>
  <template v-if="isWholesaleOrder">
    <p>
      Your custom order was placed on wholesale terms
      <tml-anchor text="(more)" @click="$overlays.open('sale-terms-popup')" />
      and as per the information provided in the online ordering process, is not
      eligible for return, refund or exchange.
    </p>
    <tml-new-popup
      name="sale-terms-popup"
      layout="standard"
      size="normal"
      height="auto"
      title="Terms of Sale"
    >
      <template #content>
        <policy-viewer url-name="b-2-b-tos" />
      </template>
    </tml-new-popup>
  </template>

  <p v-else-if="areAllItemsRefundedOrExchanged">
    All of the items in your order have already been refunded or exchanged.
  </p>

  <p v-else-if="isOrderBeingShipped">
    Your order is currently being shipped so we can't make any changes. You'll
    have to wait until it arrives and come back to this page to get information
    on how to send it back to us if you still want to make a change.
  </p>

  <p v-else-if="isOrderOldAndFaulty">
    Your order is over 28 days old so you can only return any of it's items back
    to us if they're faulty.
  </p>
  <template v-if="isReturnLabelAvailable">
    <p v-if="isReturningSomeItems">
      You've already told us you're going to be returning some of your items. We
      sent you an email containing a {{ labelType }}returns label but you can
      also download it
      <b>
        <tml-anchor
          :href="
            formatUrl(`/omnis/v3/frontend/retex/returns-label/${orderHash}/`)
          "
          :download="returnsLabelFilename"
          target="_blank"
          text="here."
        />
      </b>
    </p>
    <p v-else-if="isReturningAllItems">
      Once you've submitted what you want to return to us you'll be able to
      download a {{ labelType }}returns label. We'll also email you a copy to
      <b>{{ order.billingEmail }}</b>
      .
    </p>
  </template>
</template>
